/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableSort, type TableSortValue } from "@src/shared/constants/sort-order.constants";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { errorLogService } from "@src/@api-services/errorLog";
import type {
  AssignErrorLogBody,
  ErrorLogSearchOptions,
  IErrorLog,
  ResolveErrorLogBody,
} from "../types";
import { ERROR_LOG_STATUS } from "../constants";


interface ErrorLogsState {
  data: IErrorLog[];
  total: number;
  params: ErrorLogSearchOptions;
  selectedErrorLog: IErrorLog | null;
  loading: boolean;
  error: string | null;
}

export const getErrorLogs = createAsyncThunk(
  "errorLogs/getErrorLogs",
  async (params: ErrorLogSearchOptions, { rejectWithValue }) => {
    try {
      const response = await errorLogService.getErrorLogs(params);
      return {
        params,
        data: response.data.data.data as IErrorLog[],
        total: response.data.data.totalRecord as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch error logs"
      );
    }
  }
);

export const getErrorLog = createAsyncThunk(
  "errorLogs/getErrorLog",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await errorLogService.getErrorLog(id);
      return response.data.data as IErrorLog;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch error log"
      );
    }
  }
);

export const resolveErrorLog = createAsyncThunk(
  "errorLogs/resolveErrorLog",
  async (
    { id, notes }: { id: string | number; notes?: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const body: ResolveErrorLogBody = {};
      if (notes?.trim()) {
        body.notes = notes.trim();
      }
      const response = await errorLogService.resolveErrorLog(id, body);
      const state = getState() as any;
      await dispatch(getErrorLogs(state.errorLogs.params));
      return (response.data.data as IErrorLog) ?? { id, status: ERROR_LOG_STATUS.RESOLVED };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to resolve error log"
      );
    }
  }
);


export const assignErrorLog = createAsyncThunk(
  "errorLogs/assignErrorLog",
  async (
    { id, body }: { id: string | number; body: AssignErrorLogBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await errorLogService.assignErrorLog(id, body);
      return response.data.data as IErrorLog;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to assign error log"
      );
    }
  }
);

const initialState: ErrorLogsState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "lastOccurredAt",
    orderDirection: TableSort.DESC,
  },
  selectedErrorLog: null,
  loading: false,
  error: null,
};

export const errorLogsSlice = createSlice({
  name: "errorLogs",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedErrorLog: (state) => {
      state.selectedErrorLog = null;
    },
    setParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getErrorLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getErrorLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getErrorLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getErrorLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getErrorLog.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedErrorLog = action.payload;
      })
      .addCase(getErrorLog.rejected, (state) => {
        state.loading = false;
        state.selectedErrorLog = null;
      })
      .addCase(resolveErrorLog.fulfilled, (state, action) => {
        const updated = action.payload as IErrorLog;
        const id = String(updated.id);
        const index = state.data.findIndex((log) => String(log.id) === id);
        if (index !== -1) {
          state.data[index] = { ...state.data[index], ...updated };
        }
        if (state.selectedErrorLog && String(state.selectedErrorLog.id) === id) {
          state.selectedErrorLog = { ...state.selectedErrorLog, ...updated };
        }
      })
      .addCase(assignErrorLog.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated.id);
        const index = state.data.findIndex((log) => String(log.id) === id);
        if (index !== -1) {
          state.data[index] = { ...state.data[index], ...updated };
        }
        if (state.selectedErrorLog && String(state.selectedErrorLog.id) === id) {
          state.selectedErrorLog = updated;
        }
      });
  },
});

export const { clearError, clearSelectedErrorLog, setParams } =
  errorLogsSlice.actions;

export default errorLogsSlice.reducer;
